import React from 'react';
import { inject, observer } from 'mobx-react';
import { handleRequest } from '@/utils';
import { addDialogue } from '@/utils/sessionUtils';
import { InitStoreType } from '@/types/store/init';
import {
  TRACKING_TYPE,
  TRACKING_OPERATION,
  TRACKING_SECTION,
  TRACKING_MSG_TYPE,
  INPUT_TYPE,
} from '@/utils/constants';
import { tracker, dispatchTrack } from '@/utils/tracking';
import { TrackerProps, ChatbotUIBasicProps } from '@/types/common';
import SingleButton from '@/components/SingleButton';
import Msg from '@/components/Msg';
import ArrowRight from '@/assets/img/arrow_right.svg';
import { ReactSVG } from 'react-svg';
import Drawer from '@/components/Drawer';
import './style.scss';
import { handleClickHistoryButton, getContent } from '@/utils/tools';
import clsx from 'classnames';
import { getChildInject, isNum } from '@/utils/instantLoaded/tools';

const PREFIX = 'SPLater-component';

interface SPLaterItemBase {
  item_type: 'button_for_taskbot';
  button_for_taskbot: {
    node_id: string,
    text_key: string,
    text_value: string,
    button_id: string,
    button_type: string,
  };
}

interface ButtonInfos {
  button_id: string,
  button_type: string,
  text_value: string
}

interface SPLaterItemProps extends SPLaterItemBase {
  handleClick?: () => void;
}

interface SPLaterProps extends TrackerProps {
  title: string;
  listTitle: string;
  listConfirmButton: ButtonInfos;
  displayNum: number;
  listItems: Array<SPLaterItemBase>;
  showMoreButton: ButtonInfos,
  initStore?: InitStoreType,
  dialogueId: string
  historyDialogueId?: string,
  historySessionId?: string,
  isHistory: boolean,
  nodePointId: string;
  customCallback?: ChatbotUIBasicProps['customCallback'];
}

interface State {
  showMoreList: boolean,
  checkedConfirmItem: number | null
}

@inject(getChildInject('initStore'))
@observer
class SPLater extends React.PureComponent<SPLaterProps, State> {
  constructor(props: SPLaterProps) {
    super(props);
    this.state = {
      showMoreList: false,
      checkedConfirmItem: null,
    };

  }

  handleShowMoreBtn = () => {
    const {
      isHistory,
      listItems,
      initStore,
      dialogueId,
      nodePointId,
      showMoreButton,
    } = this.props;

    if (isHistory) {
      handleClickHistoryButton();
      return;
    }
    this.setState({ showMoreList: true });

    const drawButtonDetails = listItems.map(btn => {
      const { button_for_taskbot, button_for_taskbot: { button_id, button_type }} = btn;
      return {
        button_id,
        button_text: getContent(button_for_taskbot),
        button_type,
      };
    });

    this.trackerClickEvent({
      button_type: showMoreButton.button_type,
      button_id: showMoreButton.button_id,
      button_text: getContent(showMoreButton),
    });

    const trackingDrawerData = {
      chat_session_id: initStore?.sessionId,
      chat_dialogue_id: dialogueId,
      node_point_id: nodePointId,
      if_history: TRACKING_MSG_TYPE.REAL_TIME,
      button_details: drawButtonDetails,
    };
    dispatchTrack({
      data: { ...trackingDrawerData },
      operation: TRACKING_OPERATION.IMPRESSION,
      section: TRACKING_SECTION.standardizeButton,
    });
  };

  handleCloseDrawer = () => {
    this.resetConfirmDrawer();
  };

  choiceConfirmItem = (item: SPLaterItemProps, index: number) => {
    const { button_type, button_id } = item.button_for_taskbot;
    this.setState({ checkedConfirmItem: index });

    this.trackerClickEvent({
      button_type,
      button_id,
      button_text: getContent(item.button_for_taskbot),
    });
  };

  resetConfirmDrawer() {
    this.setState({
      showMoreList: false,
      checkedConfirmItem: null,
    });
  }

  trackerClickEvent = (data = {}) => {
    const {
      initStore,
      dialogueId,
      tracker,
      nodePointId,
    } = this.props;

    tracker.click({
      trackingname: TRACKING_TYPE.STANDARDIZE_BUTTON,
      data: {
        chat_session_id: initStore?.sessionId,
        chat_dialogue_id: dialogueId,
        node_point_id: nodePointId,
        if_history: TRACKING_MSG_TYPE.REAL_TIME,
        ...data,
      },
    });
  };

  handleConfirm = () => {
    if (this.state.checkedConfirmItem === null) {
      return;
    }
    const { listItems, listConfirmButton } = this.props;
    const button = listItems[this.state.checkedConfirmItem];
    const content = getContent(button.button_for_taskbot);
    const curPos = addDialogue({
      from: 'user',
      type: 'Msg',
      data: { content },
    });
    if (isNum(curPos)) {
      this.requestSPLaterMessage(button);
    }
    this.resetConfirmDrawer();

    this.trackerClickEvent({
      button_type: listConfirmButton.button_type,
      button_id: listConfirmButton.button_id,
      button_text: getContent(listConfirmButton),
    });
  };

  handleTopThree = (index: number) => {
    const { isHistory, listItems } = this.props;

    if (isHistory) {
      handleClickHistoryButton();
      return;
    }
    const button = listItems[index];
    const content = getContent(button.button_for_taskbot);
    const curPos = addDialogue({
      from: 'user',
      type: 'Msg',
      data: { content },
    });

    if (isNum(curPos)) {
      this.requestSPLaterMessage(button);
    }

    this.trackerClickEvent({
      button_type: button.button_for_taskbot.button_type,
      button_id: button.button_for_taskbot.button_id,
      button_text: getContent(button.button_for_taskbot),
    });
  };

  requestSPLaterMessage = (item: SPLaterItemBase) => {
    const { initStore, dialogueId, customCallback = {} } = this.props;
    handleRequest('postMessage', {
      session_id: initStore?.sessionId,
      dialogue_id: dialogueId,
      input_type: INPUT_TYPE.BUTTON,
      message_text: getContent(item.button_for_taskbot),
      message_extra: {
        node_id: item.button_for_taskbot.node_id,
        params: { order_id: 0 },
      },
      ...customCallback,
    });
  };

  render() {
    const {
      title,
      listTitle,
      listConfirmButton,
      displayNum,
      listItems,
      showMoreButton,
      passedRef,
      isHistory,
      dialogueId,
      historyDialogueId,
      historySessionId,
      initStore,
      nodePointId,
    } = this.props;

    const buttonDetails = listItems.slice(0, displayNum).map(btn => {
      const { button_id, button_type } = btn.button_for_taskbot;
      return {
        button_type,
        button_id,
        button_text: getContent(btn.button_for_taskbot),
      };
    });

    const trackingname = TRACKING_TYPE.STANDARDIZE_BUTTON;
    const trackingdataBase = {
      chat_session_id: isHistory ? historySessionId : initStore?.sessionId,
      chat_dialogue_id: isHistory ? historyDialogueId : dialogueId,
      node_point_id: nodePointId,
      if_history: isHistory ? 1 : 0,
    };
    const trackingdata = {
      ...trackingdataBase,
      button_details: buttonDetails,
    };

    return (
      <div className={PREFIX}
        ref={(ele) => passedRef(ele)}
        data-trackingname={trackingname}
        data-trackingdata={JSON.stringify({ ...trackingdata })}>

        <Msg className="message-content" content={title} />

        <div className={`${PREFIX}-list`}>
          {
            listItems.slice(0, displayNum).map((item, index) => {
              return <SPLaterItem
                {...item}
                handleClick={() => this.handleTopThree(index)}
                key={index} />;
            })
          }
        </div>

        <div className="show-more-btn" onClick={this.handleShowMoreBtn}>
          {showMoreButton?.text_value}
          <ReactSVG
            src={ArrowRight}
            className="show-more-arrow" />
        </div>

        <Drawer
          onClose={this.handleCloseDrawer}
          height="auto"
          show={this.state.showMoreList}>
          <div className="drawer-splater-container">
            <div className="drawer-splater-title">
              {listTitle}
            </div>

            <div className="drawer-splater-list">
              {
                listItems.map((item, index) => {
                  return (<label
                    key={`reason-${item.button_for_taskbot.node_id}`}
                    className={
                      clsx('option-radio-wrapper', { 'option-radio-wrapper-checked': this.state.checkedConfirmItem === index })
                    }>
                    <span className="radio">
                      <input
                        type="radio"
                        className="radio-input"
                        value={item.button_for_taskbot.text_value}
                        checked={this.state.checkedConfirmItem === index}
                        readOnly
                        onClick={() => this.choiceConfirmItem(item, index)}
                      />
                      <span className="radio-inner"></span>
                    </span>
                    <span className="content">{item.button_for_taskbot.text_value}</span>
                  </label>);
                })
              }
            </div>

            <SingleButton
              className="confirm-btn"
              disabled={this.state.checkedConfirmItem === null}
              handleClick={() => { this.handleConfirm(); }}
              text={listConfirmButton?.text_value} />
          </div>
        </Drawer>
      </div>
    );
  }
}

function SPLaterItem({ button_for_taskbot, handleClick }: SPLaterItemProps) {
  return (
    <div>
      <div className={`${PREFIX}-item`} onClick={() => handleClick?.()}>
        {button_for_taskbot?.text_value}
      </div>

      <div className={`${PREFIX}-divider`} />
    </div>
  );
}

export default tracker(SPLater);
